import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Sparkles, Zap, TrendingUp, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const ICON_BY_PLAN = { free: Sparkles, pro: Zap, business: TrendingUp };

function ProgressBar({ pct }) {
  const color = pct >= 100 ? "#E0000F" : pct >= 80 ? "#F59E0B" : "#002FA7";
  return (
    <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
      <div style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color, height: "100%" }} />
    </div>
  );
}

export default function BillingPage() {
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    const [p, m] = await Promise.all([api.get("/billing/plans"), api.get("/billing/me")]);
    setPlans(p.data.plans);
    setMe(m.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Poll status if returning from Stripe with session_id
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/billing/checkout/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Payment received — your plan is active");
          navigate("/billing", { replace: true });
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout expired");
          navigate("/billing", { replace: true });
          return;
        }
      } catch {/* keep polling */}
      if (attempts < 8) setTimeout(tick, 2000);
      else { toast.warning("Still processing — refresh later"); navigate("/billing", { replace: true }); }
    };
    tick();
  }, [params, navigate]);

  const buy = async (planId) => {
    setPending(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (data.free) {
        toast.success("Switched to Free plan");
        load();
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
    } finally {
      setPending(null);
    }
  };

  if (loading || !me) return <div className="p-12 text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>;

  const currentId = me.plan.id;

  return (
    <div data-testid="billing-page">
      <PageHeader
        title="Billing & plans"
        subtitle={`You're on the ${me.plan.name} plan. Upgrade for higher limits and advanced features.`}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-10">
        {/* Usage */}
        <div className="lift-card bg-white dark:bg-neutral-950 p-8 rounded-sm">
          <p className="label-overline mb-2">This month</p>
          <h3 className="font-display font-bold text-2xl tracking-tight mb-6">Usage</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="font-semibold">Contacts</span>
                <span className="text-neutral-500 dark:text-neutral-400">{me.usage.contacts.toLocaleString()} / {me.plan.max_contacts.toLocaleString()}</span>
              </div>
              <ProgressBar pct={me.usage.contacts_pct} />
              {me.usage.contacts_pct >= 80 && (
                <div className="text-xs mt-2 text-amber-700 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Approaching plan limit</div>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="font-semibold">Emails sent</span>
                <span className="text-neutral-500 dark:text-neutral-400">{me.usage.emails_this_month.toLocaleString()} / {me.plan.max_emails_per_month.toLocaleString()}</span>
              </div>
              <ProgressBar pct={me.usage.emails_pct} />
              {me.usage.emails_pct >= 80 && (
                <div className="text-xs mt-2 text-amber-700 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Approaching plan limit</div>
              )}
            </div>
          </div>
          {me.plan_until && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-6">Plan active until {new Date(me.plan_until).toLocaleDateString()}</p>}
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-4">
          {plans.map((p) => {
            const Icon = ICON_BY_PLAN[p.id] || Sparkles;
            const isCurrent = p.id === currentId;
            return (
              <div key={p.id} className={`lift-card bg-white dark:bg-neutral-950 p-8 rounded-sm relative ${isCurrent ? "border-2" : ""}`} style={isCurrent ? { borderColor: "var(--brand-primary)" } : {}} data-testid={`plan-card-${p.id}`}>
                {isCurrent && (
                  <span className="absolute -top-3 left-6 px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-white rounded-sm" style={{ backgroundColor: "var(--brand-primary)" }}>Current</span>
                )}
                <div className="h-10 w-10 rounded-sm flex items-center justify-center mb-6" style={{ backgroundColor: "var(--bg-muted)" }}>
                  <Icon className="h-5 w-5 text-neutral-700 dark:text-neutral-300" />
                </div>
                <h3 className="font-display font-bold text-2xl tracking-tight mb-1">{p.name}</h3>
                <div className="font-display font-black text-4xl tracking-tighter mb-1">
                  ${p.price}<span className="text-neutral-400 text-base font-bold">/mo</span>
                </div>
                <ul className="space-y-2 text-sm text-neutral-600 dark:text-neutral-400 mt-6 mb-6">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5" style={{ color: "var(--accent-green)" }} /> {p.max_contacts.toLocaleString()} contacts</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5" style={{ color: "var(--accent-green)" }} /> {p.max_emails_per_month.toLocaleString()} emails / month</li>
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5" style={{ color: "var(--accent-green)" }} /> {f}</li>
                  ))}
                </ul>
                <Button
                  onClick={() => buy(p.id)}
                  disabled={isCurrent || pending === p.id}
                  className={`w-full rounded-sm font-semibold ${isCurrent ? "" : "btn-brand"}`}
                  variant={isCurrent ? "outline" : "default"}
                  data-testid={`plan-buy-${p.id}`}
                >
                  {isCurrent ? "Current plan" : pending === p.id ? "Processing..." : p.price === 0 ? "Switch to Free" : `Upgrade to ${p.name}`}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
